import {
  Dispatch,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { organisationApi, OrganisationUserManagementStatus, UserRole } from '../../../../services';
import { Button, ButtonType, Modal, Radio, Select, useAppSelector } from '../../../../shared';

import classes from './UpdateUsersRoleModal.module.scss';

interface UpdateUsersRoleModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  userIds: number[];
  callback: () => void;
  channelId?: number;
}

export const UpdateUsersRoleModal: FunctionComponent<UpdateUsersRoleModalProps> = memo(
  ({ isOpen, setIsOpen, userIds, callback, channelId }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsersUpdateRoleModal' });

    const [organisationUserUpdateChannelRole] =
      organisationApi.endpoints.organisationUserUpdateChannelRole.useMutation();

    const [organisationChannelUserUpdateChannelRole] =
      organisationApi.endpoints.organisationChannelUserUpdateChannelRole.useMutation();

    const { channels } = useAppSelector(({ channels }) => channels);

    const [channelIds, setChannelIds] = useState<number[]>([]);

    const [role, setRole] = useState<UserRole>(UserRole.CHANNEL_READER);

    const [loading, setLoading] = useState<boolean>(false);

    const userIdsLength = useMemo(() => userIds.length, [userIds.length]);

    const channelDropdownItems = useMemo(
      () =>
        channels.map(({ id, name }) => {
          return { id, title: name };
        }),
      [channels]
    );

    const onClose = useCallback(() => {
      setIsOpen(false);
      setChannelIds([]);
      setRole(UserRole.CHANNEL_READER);
    }, [setIsOpen]);

    const updateRoleHandler = useCallback(async () => {
      setLoading(true);

      const request = channelId
        ? organisationChannelUserUpdateChannelRole({ channelId, userIds, role })
        : organisationUserUpdateChannelRole({ channelIds, userIds, role });

      try {
        const userManagementResult = await request.unwrap();

        if (userManagementResult.length) {
          const hasSkipOrAccess = userManagementResult.find(({ status }) =>
            [
              OrganisationUserManagementStatus.SKIP,
              OrganisationUserManagementStatus.ACCESS,
            ].includes(status)
          );

          hasSkipOrAccess ? toast.info(ctxT('update-skip')) : toast.success(ctxT('update-success'));

          callback();
          onClose();
          return;
        }

        toast.error(t('common.error-message'));
      } catch (_) {
        toast.error(t('common.error-message'));
      } finally {
        setLoading(false);
      }
    }, [
      callback,
      channelId,
      channelIds,
      ctxT,
      onClose,
      organisationChannelUserUpdateChannelRole,
      organisationUserUpdateChannelRole,
      role,
      t,
      userIds,
    ]);

    const title = ctxT('update-users-role', { count: userIdsLength });

    const body = useMemo(() => {
      return (
        <div className={classes['update-role']}>
          <div className={classes['update-role__title']}>{title}</div>

          <div className={classes['update-role__role']}>
            <div className={classes['update-role__role-label']}>{t('common.role')}</div>
            {Object.values(UserRole)
              .filter((value) => value.startsWith('CHANNEL_'))
              .map((value) => {
                return (
                  <Radio
                    key={value}
                    checked={value === role}
                    name={'update-role'}
                    label={t(`userRoles.${value}`)}
                    onChange={() => setRole(value)}
                  />
                );
              })}
          </div>

          {!channelId && (
            <Select
              label={t('common.channel')}
              selectedItemIds={channelIds}
              items={channelDropdownItems}
              onMultiCheckChange={setChannelIds}
              staticListPosition
            />
          )}

          <Button
            type={ButtonType.primary}
            label={t('common.update')}
            className={classes['update-role__button']}
            onClick={updateRoleHandler}
            disabled={Boolean(loading || (!channelId && !channelIds.length))}
            loading={loading}
          />
        </div>
      );
    }, [channelDropdownItems, channelId, channelIds, loading, role, t, title, updateRoleHandler]);

    if (!userIdsLength) {
      return null;
    }

    return <Modal isOpen={isOpen} body={body} onClose={onClose} ariaLabel={title} alignTop />;
  }
);
